import type { M365ReleaseItem, M365Product, M365TagContainer } from '../types/m365';

/**
 * Use proxy in development (avoids CORS); in production set REACT_APP_M365_API_URL
 * to your backend proxy URL if the API is still blocked.
 */
const M365_API_URL ="https://smartdataproxy.azurewebsites.net/api/CachedProxy?target=https://www.microsoft.com/releasecommunications/api/v1/m365";

/**
 * Singleton manager for Microsoft 365 release / roadmap API operations.
 */
export class MSRoadMapManager {
    private static instance: MSRoadMapManager;

    /** Cached result of fetchM365Releases(); only fetched from API when empty. */
    private cachedReleases: M365ReleaseItem[] | null = null;

    private constructor() {}

    static getInstance(): MSRoadMapManager {
        if (!MSRoadMapManager.instance) {
            MSRoadMapManager.instance = new MSRoadMapManager();
        }
        return MSRoadMapManager.instance;
    }

    /**
     * Resolves display name for a product (API may use productName, tagName, name, title, or label).
     */
    getProductDisplayName(p: M365Product): string {
        if (typeof p === 'string') return p;
        if (p == null) return '';
        const o = p as Record<string, unknown>;
        const name =
            typeof o.productName === 'string'
                ? o.productName
                : typeof o.tagName === 'string'
                  ? o.tagName
                  : typeof o.name === 'string'
                    ? o.name
                    : typeof o.title === 'string'
                      ? o.title
                      : typeof o.label === 'string'
                        ? o.label
                        : '';
        return name || (typeof o === 'object' ? JSON.stringify(p) : String(p));
    }

    /**
     * Fetches Microsoft 365 release and feature update entries from the official API.
     * Results are cached; the REST endpoint is only called when the cache is empty.
     */
    async fetchM365Releases(): Promise<M365ReleaseItem[]> {
        if (this.cachedReleases !== null) {
            return this.cachedReleases;
        }

        const response = await fetch(M365_API_URL);
        if (!response.ok) {
            throw new Error(`M365 API error: ${response.status}`);
        }
        const data = await response.json();
        if (!Array.isArray(data)) {
            this.cachedReleases = [];
            return [];
        }
        this.cachedReleases = data as M365ReleaseItem[];
        return this.cachedReleases;
    }

    /**
     * Collects all unique product values from tagContainer/tagsContainer.products across items.
     */
    getUniqueProducts(items: M365ReleaseItem[]): string[] {
        const set = new Set<string>();
        for (const item of items) {
            const container = (item.tagContainer ?? item.tagsContainer) as M365TagContainer | undefined;
            const products = container?.products;
            if (!Array.isArray(products)) continue;
            for (const p of products) {
                const name = this.getProductDisplayName(p);
                if (name) set.add(name);
            }
        }
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }

    /**
     * Collects all unique releasePhase tagName values from tagContainer/tagsContainer.releasePhase across items.
     */
    getUniqueReleasePhases(items: M365ReleaseItem[]): string[] {
        const set = new Set<string>();
        for (const item of items) {
            const container = (item.tagContainer ?? item.tagsContainer) as M365TagContainer | undefined;
            const releasePhase = container?.releasePhase;
            if (!Array.isArray(releasePhase)) continue;
            for (const entry of releasePhase) {
                const o = entry as { tagName?: string };
                const name = typeof o?.tagName === 'string' ? o.tagName.trim() : '';
                if (name) set.add(name);
            }
        }
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }

    /**
     * Collects all unique platforms tagName values from tagContainer/tagsContainer.platforms across items.
     */
    getUniquePlatforms(items: M365ReleaseItem[]): string[] {
        const set = new Set<string>();
        for (const item of items) {
            const container = (item.tagContainer ?? item.tagsContainer) as M365TagContainer | undefined;
            const platforms = container?.platforms;
            if (!Array.isArray(platforms)) continue;
            for (const entry of platforms) {
                const o = entry as { tagName?: string };
                const name = typeof o?.tagName === 'string' ? o.tagName.trim() : '';
                if (name) set.add(name);
            }
        }
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }

    /**
     * Collects all unique status values from items (item.status).
     */
    getUniqueStatuses(items: M365ReleaseItem[]): string[] {
        const set = new Set<string>();
        for (const item of items) {
            const s = item.status;
            const name = typeof s === 'string' ? s.trim() : '';
            if (name) set.add(name);
        }
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }
}
